import { OrganizationType } from "@prisma/client";
import type { Capability } from "@remi/enums/capability";
import { Channel } from "@remi/types/channels";

import { hasCapability, type User } from "../base";

const CHANNEL_VIEW_CAP = {
  REMI_INTERNAL: "message.channel.view.remiInternal",
  CONTRACTOR: "message.channel.view.contractor",
  SOLAR_COMPANY: "message.channel.view.solarCompany",
  HOMEOWNER: "message.channel.view.homeowner",
  // null preserves today's behavior: CONTRACTOR_PRODUCTION_MESSAGES is unviewable
  // for every role. Mapping it to view.contractor would silently grant access
  // to CONTRACTOR_ADMIN and REMI_USER.
  CONTRACTOR_PRODUCTION_MESSAGES: null,
} as const satisfies Record<Channel, Capability | null>;

export const message = {
  delete: (
    user: User,
    message: {
      conversationItem: {
        personId: string;
      };
      deletedAt: Date | null;
    },
  ) => {
    if (message.deletedAt === null && hasCapability(user, "message.delete.all")) {
      return true;
    }
    return hasCapability(user, "message.delete.owned") && user.person.id === message.conversationItem.personId;
  },
  restore: (
    user: User,
    message: {
      conversationItem: {
        personId: string;
      };
      deletedAt: Date | null;
    },
  ) => {
    if (message.deletedAt !== null && hasCapability(user, "message.restore.all")) {
      return true;
    }
    return hasCapability(user, "message.restore.owned") && user.person.id === message.conversationItem.personId;
  },
  edit: (
    user: User,
    message: {
      conversationItem: {
        personId: string;
      };
      deletedAt: Date | null;
    },
  ) => {
    return message.deletedAt === null && user.person.id === message.conversationItem.personId;
  },
  broadcast: (user: User) => hasCapability(user, "message.broadcast"),

  create: (user: User) => hasCapability(user, "message.create"),
};

export const canViewChannel = (user: User, channel: Channel): boolean => {
  const cap = CHANNEL_VIEW_CAP[channel];
  return cap !== null && hasCapability(user, cap);
};

export const filterViewableChannels = (user: User, channels: readonly Channel[]): Channel[] =>
  channels.filter((channel) => canViewChannel(user, channel));

const DEFAULT_CHANNEL_PRIORITY = [
  Channel.REMI_INTERNAL,
  Channel.CONTRACTOR,
  Channel.SOLAR_COMPANY,
] as const satisfies readonly Channel[];

export const getDefaultChannel = (user: User): Channel => {
  for (const channel of DEFAULT_CHANNEL_PRIORITY) {
    if (canViewChannel(user, channel)) {
      return channel;
    }
  }
  return Channel.CONTRACTOR;
};

export const getDefaultChannelOrganizationId = (user: User): string | null =>
  hasCapability(user, "message.defaultChannel.orgScoped") ? user.person.organizationId : null;

export const ChannelToOrgsThatCanViewThatChannel: Record<Channel, OrganizationType[]> = {
  REMI_INTERNAL: [OrganizationType.REMI_INTERNAL],
  CONTRACTOR: [OrganizationType.CONTRACTOR, OrganizationType.REMI_INTERNAL],
  SOLAR_COMPANY: [OrganizationType.SOLAR_COMPANY, OrganizationType.REMI_INTERNAL],
  HOMEOWNER: [],
  CONTRACTOR_PRODUCTION_MESSAGES: [],
};
